/**
 * EChartsRenderer - thin React wrapper around Apache ECharts v6.
 *
 * Uses the ECharts API directly (no echarts-for-react wrapper) for full
 * control and zero third-party-wrapper dependency lag on major versions.
 *
 * v6 features leveraged:
 *  - Dynamic theme switching via setTheme() (no dispose/re-init)
 *  - Improved default axis label overflow prevention
 *  - New default theme (overridden by our own 'klaris' theme)
 */

import React from 'react';
import * as echarts from 'echarts/core';
import {
  BarChart, LineChart, PieChart, ScatterChart, EffectScatterChart, HeatmapChart,
  FunnelChart, GaugeChart, TreemapChart, BoxplotChart,
  RadarChart, CandlestickChart, SankeyChart, SunburstChart,
  PictorialBarChart, ThemeRiverChart, ParallelChart,
  MapChart,
} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  VisualMapComponent,
  ToolboxComponent,
  GraphicComponent,
  MarkLineComponent,
  MarkPointComponent,
  MarkAreaComponent,
  ParallelComponent,
  SingleAxisComponent,
  RadarComponent,
  GeoComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { buildEChartsTheme } from '../theme/charts';
import { downloadChartImage, ChartImageFormat } from '../utils/chartDownload';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { TenantBranding } from '../hooks/useBranding';
import { ModeContext } from '../theme/ThemeProvider';
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Image, PhotoCamera, Code } from '@mui/icons-material';

// Register ECharts components (tree-shakeable)
echarts.use([
  BarChart, LineChart, PieChart, ScatterChart, EffectScatterChart, HeatmapChart,
  FunnelChart, GaugeChart, TreemapChart, BoxplotChart,
  RadarChart, CandlestickChart, SankeyChart, SunburstChart,
  PictorialBarChart, ThemeRiverChart, ParallelChart,
  MapChart,
  TitleComponent, TooltipComponent, GridComponent, LegendComponent,
  DataZoomComponent, VisualMapComponent, ToolboxComponent, GraphicComponent,
  MarkLineComponent, MarkPointComponent, MarkAreaComponent,
  ParallelComponent, SingleAxisComponent, RadarComponent,
  GeoComponent,
  CanvasRenderer,
]);

// ---------------------------------------------------------------------------
// Map support: lazy-load GeoJSON and register with ECharts
// ---------------------------------------------------------------------------

const MAP_GEOJSON_URLS: Record<string, string> = {
  USA: '/maps/usa.json',
};

// Module-level cache - maps are registered once per page load
const _registeredMaps = new Set<string>();

async function ensureMapRegistered(mapName: string): Promise<void> {
  if (_registeredMaps.has(mapName)) return;
  const url = MAP_GEOJSON_URLS[mapName];
  if (!url) throw new Error(`No GeoJSON available for map: ${mapName}`);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch map GeoJSON for ${mapName}: ${res.status}`);
  const geoJson = await res.json();
  echarts.registerMap(mapName, geoJson as any);
  _registeredMaps.add(mapName);
}

function getMapName(option: Record<string, any>): string | null {
  const series = option?.series;
  if (Array.isArray(series)) {
    for (const s of series) {
      if (s?.type === 'map' && s?.map) return String(s.map);
    }
  }
  if (option?.geo?.map) return String(option.geo.map);
  // Also read the _mapName hint set by _ChoroplethBuilder
  if (option?._mapName) return String(option._mapName);
  return null;
}

// Theme registration cache. Keyed by mode + branding so dark/light produces
// different registered themes; v6 lets us swap themes via setTheme() without
// re-initialising the chart instance.
let _themeRegistered = false;
let _lastThemeKey = '';

function ensureTheme(mode: 'light' | 'dark', branding?: TenantBranding | null) {
  const key = `${mode}|${JSON.stringify(branding?.colors?.charts || 'default')}`;
  if (!_themeRegistered || key !== _lastThemeKey) {
    echarts.registerTheme('klaris', buildEChartsTheme(mode, branding));
    _themeRegistered = true;
    _lastThemeKey = key;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EChartsRendererProps {
  option: Record<string, any>;
  branding?: TenantBranding | null;
  height?: number | string;
  onChartReady?: (instance: echarts.ECharts) => void;
  /** If true, fills the parent container height */
  fillContainer?: boolean;
}

// ---------------------------------------------------------------------------
// Component - uses ECharts v6 API directly
// ---------------------------------------------------------------------------

export default function EChartsRenderer({
  option,
  branding,
  height,
  onChartReady,
  fillContainer = false,
}: EChartsRendererProps) {
  // Use backend-computed adaptive height, then explicit prop, then default
  const resolvedHeight = fillContainer
    ? undefined  // fillContainer uses 100% CSS
    : (height ?? option?._chartHeight ?? 420);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const instanceRef = React.useRef<echarts.ECharts | null>(null);
  const [exportMode, setExportMode] = React.useState(false);
  const [imgUrl, setImgUrl] = React.useState<string | null>(null);

  // For map charts: track whether GeoJSON has been fetched and registered.
  // Initialise synchronously to true if the map is already cached so that
  // the chart <div> renders on the first paint (avoids ResizeObserver miss).
  const mapName = React.useMemo(() => getMapName(option), [option]);
  const [mapReady, setMapReady] = React.useState(() => {
    const name = getMapName(option);
    return !name || _registeredMaps.has(name);
  });

  // Track current UI mode so the chart theme picks dark/light tokens. The
  // legacy hard-coded 'light' theme caused unreadable (visually "blurry")
  // labels on dark backgrounds.
  const { mode } = React.useContext(ModeContext);

  // Ensure theme is registered for the current mode + branding combo.
  ensureTheme(mode, branding);

  // Resolve JS function strings in the option (e.g., axis formatters)
  const resolvedOption = React.useMemo(() => {
    return reviveJsFunctions(option);
  }, [option]);

  // --- Pre-load GeoJSON for map charts before first render ---
  React.useEffect(() => {
    if (!mapName) {
      setMapReady(true);
      return;
    }
    let cancelled = false;
    setMapReady(false);
    ensureMapRegistered(mapName)
      .catch(err => console.error('[EChartsRenderer] Map load failed:', err))
      .finally(() => { if (!cancelled) setMapReady(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapName]);

  // --- Core lifecycle: init, setOption, dispose ---
  React.useEffect(() => {
    if (!mapReady) return;
    const el = containerRef.current;
    if (!el) return;

    // Init instance with our custom theme. Pin `devicePixelRatio` so the
    // canvas is rasterised at the display's native resolution — without this
    // some browsers fall back to ratio 1 inside transformed/scrolled
    // containers, which makes text and thin strokes look soft on retina.
    const chart = echarts.init(el, 'klaris', {
      renderer: 'canvas',
      devicePixelRatio: typeof window !== 'undefined'
        ? Math.max(window.devicePixelRatio || 1, 2)
        : 2,
    });
    instanceRef.current = chart;
    chart.setOption(resolvedOption, true /* notMerge */);
    onChartReady?.(chart);

    // Long category names (campaigns, accounts, SKUs) are clipped to fit the
    // axis gutter, which hides the part that tells them apart. The backend
    // keeps the untruncated value on the axis and sets axisLabel.triggerEvent
    // where it clipped, so hovering a label can show the whole thing.
    const detach = attachAxisLabelTooltip(chart, el);

    return () => {
      detach();
      chart.dispose();
      instanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]); // re-init when map becomes ready

  // Latest resolved option, accessed by the theme-switch effect without
  // making it re-run on every option change.
  const latestOptionRef = React.useRef(resolvedOption);
  React.useEffect(() => {
    latestOptionRef.current = resolvedOption;
  }, [resolvedOption]);

  // --- Update option when it changes ---
  React.useEffect(() => {
    const chart = instanceRef.current;
    if (!chart || chart.isDisposed()) return;
    chart.setOption(resolvedOption, true /* notMerge */);
  }, [resolvedOption]);

  // --- v6 dynamic theme switching when branding or mode changes ---
  React.useEffect(() => {
    const chart = instanceRef.current;
    if (!chart || chart.isDisposed()) return;
    ensureTheme(mode, branding);
    // v6: setTheme() allows switching without dispose/re-init
    if (typeof (chart as any).setTheme === 'function') {
      (chart as any).setTheme('klaris');
      // Re-apply option so series-level styles (label color, etc.) pick up
      // the new theme — setTheme alone doesn't redraw existing series.
      chart.setOption(latestOptionRef.current, true /* notMerge */);
    }
  }, [mode, branding]);

  // --- Responsive resize via ResizeObserver ---
  // Depends on mapReady: for map charts the chart <div> isn't mounted until
  // mapReady=true, so containerRef.current is null on the initial mount effect.
  // Including mapReady ensures the observer re-attaches once the div appears.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      try {
        const chart = instanceRef.current;
        if (chart && !chart.isDisposed()) chart.resize();
      } catch { /* ignore */ }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mapReady]); // re-attach when chart div becomes available

  // --- Chat export events (same pattern as old ChartRenderer) ---
  React.useEffect(() => {
    function onPrepare() {
      try {
        const chart = instanceRef.current;
        if (!chart || chart.isDisposed()) return;
        const url = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
        setImgUrl(url);
        setExportMode(true);
      } catch { /* ignore */ }
    }
    function onRestore() {
      setExportMode(false);
      setImgUrl(null);
    }
    window.addEventListener('chat:export:prepare', onPrepare as EventListener);
    window.addEventListener('chat:export:restore', onRestore as EventListener);
    return () => {
      window.removeEventListener('chat:export:prepare', onPrepare as EventListener);
      window.removeEventListener('chat:export:restore', onRestore as EventListener);
    };
  }, []);

  const style: React.CSSProperties = fillContainer
    ? { width: '100%', height: '100%', minHeight: 200 }
    : { width: '100%', height: typeof resolvedHeight === 'number' ? resolvedHeight : resolvedHeight };

  if (exportMode && imgUrl) {
    return (
      <div style={style}>
        <img src={imgUrl} alt="chart" style={{ width: '100%', height: 'auto' }} />
      </div>
    );
  }

  if (!mapReady) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>Loading map…</div>;
  }

  // position:relative so the axis-label tooltip can be placed against this box
  return <div ref={containerRef} style={{ position: 'relative', ...style }} />;
}

// ---------------------------------------------------------------------------
// Download button (mirrors ChartDownloadButton from old renderer)
// ---------------------------------------------------------------------------

export function EChartsDownloadButton({
  chartInstance,
  size = 'small',
  variant = 'default',
  title,
}: {
  chartInstance: echarts.ECharts | null;
  size?: 'small' | 'medium';
  variant?: 'default' | 'purple';
  /** Names the downloaded file; falls back to "chart". */
  title?: string;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { notify } = React.useContext(SnackbarContext);

  const download = (format: ChartImageFormat) => {
    try {
      downloadChartImage(chartInstance, format, title);
    } catch (err: any) {
      console.error('Chart download failed:', err);
      notify(err?.message || 'Could not download the chart.', 'error');
    }
    setAnchorEl(null);
  };

  const isPurple = variant === 'purple';

  return (
    <>
      <Tooltip title="Download chart">
        <IconButton
          size={size}
          onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
          sx={{
            p: 0.75,
            ...(isPurple && {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': { backgroundColor: 'primary.dark' },
            }),
          }}
        >
          <img
            src={isPurple ? '/icons/download-white.svg' : '/icons/download.svg'}
            alt="download"
            style={{ width: 18, height: 18, display: 'block' }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, minWidth: 180 } }}
      >
        <MenuItem onClick={() => download('png')}>
          <ListItemIcon><Image fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download as PNG" />
        </MenuItem>
        <MenuItem onClick={() => download('jpeg')}>
          <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download as JPEG" />
        </MenuItem>
        <MenuItem onClick={() => download('svg')}>
          <ListItemIcon><Code fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download as SVG" />
        </MenuItem>
      </Menu>
    </>
  );
}

// ---------------------------------------------------------------------------
// Utility: reveal a clipped axis label on hover
// ---------------------------------------------------------------------------
// ECharts truncates a long category label to fit its gutter but has no built-in
// way to show the rest. With `axisLabel.triggerEvent` the label emits pointer
// events carrying `value` - the full, untruncated category - so we can render
// it ourselves. Styled to match the ECharts tooltip so it reads as one system.

function attachAxisLabelTooltip(chart: echarts.ECharts, host: HTMLElement): () => void {
  let tip: HTMLDivElement | null = null;

  const hide = () => {
    if (tip) { tip.remove(); tip = null; }
  };

  const show = (text: string, ev: { offsetX?: number; offsetY?: number } | undefined) => {
    if (!text) return;
    if (!tip) {
      tip = document.createElement('div');
      tip.setAttribute('role', 'tooltip');
      Object.assign(tip.style, {
        position: 'absolute',
        zIndex: '10',
        pointerEvents: 'none',
        maxWidth: '380px',
        padding: '6px 9px',
        borderRadius: '4px',
        background: 'rgba(50,50,50,0.92)',
        color: '#fff',
        font: '12px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif',
        wordBreak: 'break-word',
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
      } as CSSStyleDeclaration);
      host.appendChild(tip);
    }
    tip.textContent = text;
    // Place near the cursor, then pull back inside the chart box so a label on
    // the right edge doesn't push the tooltip out of view.
    const x = (ev?.offsetX ?? 0) + 14;
    const y = (ev?.offsetY ?? 0) + 14;
    tip.style.left = '0px';
    tip.style.top = '0px';
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    tip.style.left = Math.max(4, Math.min(x, host.clientWidth - w - 4)) + 'px';
    tip.style.top = Math.max(4, Math.min(y, host.clientHeight - h - 4)) + 'px';
  };

  // Track the pointer off zrender rather than reading it from the event that
  // comes with `mouseover`: on component (non-series) targets that event does
  // not always carry a native DOM event, and reading offsetX off `undefined`
  // silently places the tooltip at 0,0.
  const pointer = { offsetX: 0, offsetY: 0 };
  const zr = chart.getZr();
  const onMove = (e: any) => {
    pointer.offsetX = e?.offsetX ?? 0;
    pointer.offsetY = e?.offsetY ?? 0;
  };
  zr.on('mousemove', onMove);

  const onOver = (params: any) => {
    if (params?.targetType !== 'axisLabel') return;
    const value = params.value ?? params.name;
    if (value == null) return;
    show(String(value), pointer);
  };

  chart.on('mouseover', onOver);
  chart.on('mouseout', hide);
  chart.on('globalout', hide);

  return () => {
    zr.off('mousemove', onMove);
    chart.off('mouseover', onOver);
    chart.off('mouseout', hide);
    chart.off('globalout', hide);
    hide();
  };
}

// ---------------------------------------------------------------------------
// Utility: revive JS function strings from backend
// ---------------------------------------------------------------------------
// The Python chart_spec_builder emits axis formatters as plain strings
// (e.g. "function(v){...}").  We walk the option tree and convert any
// string that starts with "function(" into a real JS function via
// new Function().  This is safe because the strings originate from our
// own deterministic builder, not from user input.

function reviveJsFunctions(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') {
    if (obj.startsWith('function(') || obj.startsWith('function (')) {
      try {
        // eslint-disable-next-line no-new-func
        return new Function('return (' + obj + ')')();
      } catch {
        return obj;
      }
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(reviveJsFunctions);
  }
  if (typeof obj === 'object') {
    const out: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
      out[key] = reviveJsFunctions(obj[key]);
    }
    return out;
  }
  return obj;
}
